import React, { useState } from "react";
import {
  Paper,
  Typography,
  Box,
  Chip,
  Stack,
  alpha,
  Divider,
  IconButton,
  Collapse,
  Tooltip,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import Checkbox from "@mui/material/Checkbox";
import { keyframes } from "@emotion/react";

const disposedFadeIn = keyframes`
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const textFadeSlide = keyframes`
  from { opacity: 0; transform: translateX(-16px); }
  to { opacity: 1; transform: translateX(0); }
`;

const AssetStatusPanel = ({
  selectedItem,
  yearOfAssessment,
  deferredYears = [],
  onDeferralChange,
  onDisposedToggle,
}) => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(true);
  const [animating, setAnimating] = useState(false);

  if (!selectedItem?.principalDetails) {
    return null;
  }

  let allYears = Object.keys(selectedItem.principalDetails || {})
    .map(Number)
    .filter((year) =>
      selectedItem?.isCurrentYearDisposed ? year !== yearOfAssessment : true,
    )
    .sort((a, b) => a - b);

  const handleYearToggle = (year) => {
    const newDeferredYears = deferredYears.includes(year)
      ? deferredYears.filter((y) => y !== year)
      : [...deferredYears, year].sort((a, b) => a - b);

    onDeferralChange(newDeferredYears);
  };

  const handleDisposedClick = () => {
    if (!selectedItem?.isCurrentYearDisposed) {
      setAnimating(true);
      setTimeout(() => setAnimating(false), 600);
    }
    onDisposedToggle();
  };

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 2,
        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.05),
        border: "1px solid",
        borderColor: (theme) => alpha(theme.palette.primary.main, 0.5),
        overflow: "hidden",
      }}
    >
      {/* Header with expand/collapse */}
      <Box
        sx={{
          p: 3,
          pb: expanded ? 3 : 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: "pointer",
          "&:hover": {
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.02),
          },
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Box display='flex' alignItems='center' gap={2}>
          <Box display='flex' alignItems='center' gap={1}>
            <PauseCircleOutlineIcon color='primary' fontSize='small' />
            <Typography
              variant='subtitle1'
              fontWeight={600}
              color='primary.main'
            >
              Asset Status & Deferral
            </Typography>
          </Box>

          {/* Disposal Status */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              position: "relative",
              height: 32,
            }}
          >
            <Checkbox
              checked={selectedItem?.isCurrentYearDisposed || false}
              onChange={(e) => {
                e.stopPropagation();
                handleDisposedClick();
              }}
              color='error'
              size='small'
              sx={{
                p: 0.5,
                transition: "background 0.4s, color 0.4s, box-shadow 0.4s",
                bgcolor: (theme) =>
                  selectedItem?.isCurrentYearDisposed || false
                    ? animating
                      ? alpha(theme.palette.error.dark, 0.2)
                      : alpha(theme.palette.error.dark, 0.08)
                    : undefined,
                boxShadow: animating
                  ? "0 0 12px 2px rgba(183,28,28,0.24)"
                  : undefined,
                borderRadius: 1,
              }}
              onClick={(e) => e.stopPropagation()}
            />
            <Box
              sx={{
                minWidth: 90,
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
              }}
            >
              {!selectedItem?.isCurrentYearDisposed && (
                <Typography
                  key='notdisposed'
                  variant='body2'
                  color='text.secondary'
                  sx={{
                    ml: 1,
                    opacity: 1,
                    animation: `${textFadeSlide} 0.5s`,
                    fontWeight: 500,
                    transition: "color 0.3s",
                  }}
                >
                  Not disposed
                </Typography>
              )}
              {selectedItem?.isCurrentYearDisposed && (
                <Tooltip title='Disposed in current year'>
                  <Chip
                    label='Disposed'
                    color='error'
                    size='small'
                    sx={{
                      cursor: "default",
                      ml: 1,
                      opacity: animating ? 0 : 1,
                      animation: animating
                        ? `${disposedFadeIn} 0.6s ease-in-out 0.2s forwards`
                        : undefined,
                    }}
                  />
                </Tooltip>
              )}
            </Box>
          </Box>

          {/* Deferral Summary */}
          {deferredYears.length > 0 && (
            <Chip
              label={`${deferredYears.length} deferred`}
              color='warning'
              size='small'
              variant='filled'
            />
          )}
          {!expanded && deferredYears.length > 0 && (
            <Typography variant='body2' color='text.secondary'>
              ({deferredYears.length} years deferred)
            </Typography>
          )}
        </Box>

        <IconButton
          size='small'
          onClick={(e) => {
            e.stopPropagation();
            setExpanded(!expanded);
          }}
          sx={{
            color: "primary.main",
            "&:hover": {
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
            },
          }}
        >
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>

      <Collapse in={expanded}>
        <Box sx={{ px: 3, pb: 3 }}>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 1.5 }}>
            Set the disposal status of assets and choose the years to defer.
            Deferred years will be labeled as <strong>"Deferred"</strong>.
          </Typography>

          {selectedItem?.isCurrentYearDisposed && (
            <Typography
              variant='body2'
              color='text.secondary'
              sx={{ mb: 3, fontStyle: "italic" }}
            >
              Note: If an asset is disposed, the year of assessment cannot be
              deferred.
            </Typography>
          )}

          <Stack spacing={3}>
            {/* Year Deferral Section */}
            <Box>
              <Typography
                variant='body2'
                fontWeight={600}
                color='text.secondary'
                sx={{ mb: 1 }}
              >
                Available Years for Deferral:
              </Typography>
              <Stack direction='row' spacing={1} flexWrap='wrap' useFlexGap>
                {allYears.map((year) => {
                  const isDeferred = deferredYears.includes(year);

                  const isDisposed =
                    selectedItem?.isCurrentYearDisposed &&
                    year > yearOfAssessment;

                  if (isDisposed) return;
                  return (
                    <Chip
                      key={year}
                      label={year.toString()}
                      variant={isDeferred ? "filled" : "outlined"}
                      color={isDeferred ? "warning" : "default"}
                      onClick={() => handleYearToggle(year)}
                      sx={{
                        cursor: "pointer",
                        "&:hover": {
                          bgcolor: isDeferred
                            ? (theme) => alpha(theme.palette.warning.main, 0.8)
                            : (theme) => alpha(theme.palette.primary.main, 0.1),
                        },
                      }}
                    />
                  );
                })}
              </Stack>
            </Box>

            <Divider />

            {/* Deferral Summary */}
            <Box>
              <Typography
                variant='body2'
                fontWeight={600}
                color='text.secondary'
                sx={{ mb: 1 }}
              >
                Deferral Summary:
              </Typography>
              {deferredYears.length > 0 ? (
                <Stack spacing={1}>
                  <Stack direction='row' spacing={1} flexWrap='wrap' useFlexGap>
                    {deferredYears.map((year) => {
                      return (
                        <Chip
                          key={year}
                          label={year.toString()}
                          color='warning'
                          variant='filled'
                          size='small'
                          onClick={() => handleYearToggle(year)}
                          sx={{
                            cursor: "pointer",
                            "&:hover": {
                              bgcolor: (theme) =>
                                alpha(theme.palette.warning.main, 0.8),
                            },
                          }}
                        />
                      );
                    })}
                  </Stack>
                  <Typography
                    variant='body2'
                    fontWeight={600}
                    color='warning.main'
                    sx={{ mt: 1 }}
                  >
                    Total Deferred: {deferredYears.length} years
                  </Typography>
                </Stack>
              ) : (
                <Typography
                  variant='body2'
                  color='text.secondary'
                  fontStyle='italic'
                >
                  No years selected for deferral
                </Typography>
              )}
            </Box>
          </Stack>
        </Box>
      </Collapse>
    </Paper>
  );
};

export default AssetStatusPanel;
